<template>
  <AIVDialog :visible.sync="dialogVisible" title="Configure Required Compliance Forms" width="500px">
    <div class="compliance-config">
      <p class="compliance-config__subtitle">
        Select which compliance forms are required for <strong>{{ clientName }}</strong>.
        Unchecking all forms resets to the default (all forms required).
      </p>

      <div v-if="loading" class="text-center py-3">
        <i class="fa fa-spinner fa-spin" style="color: #fff;"></i>
        <span style="color: #fff;"> Loading...</span>
      </div>

      <div v-else>
        <el-checkbox-group v-model="selectedFormIds">
          <el-checkbox
            v-for="f in allForms"
            :key="f.id"
            :label="f.id"
            class="form-checkbox"
          >
            {{ f.display_name }}
          </el-checkbox>
        </el-checkbox-group>

        <div class="compliance-config__actions mt-2">
          <el-button type="text" size="small" @click="toggleSelectAll">
            {{ allSelected ? 'Deselect All' : 'Select All' }}
          </el-button>
          <el-button
            v-if="isConfigured"
            type="text"
            size="small"
            style="color: #e6a23c;"
            @click="resetToDefault"
          >
            Reset to Default (All Forms)
          </el-button>
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="save" :loading="saving">
          <i class="fa fa-save"></i> Save Configuration
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import { complianceFormsService } from "@/serviceHandlers/complianceFormsService";

export default {
  name: "ClientComplianceConfigModal",
  props: {
    visible: { type: Boolean, default: false },
    clientId: { type: [Number, String], default: null },
    clientName: { type: String, default: "" },
  },
  data() {
    return {
      allForms: [],
      selectedFormIds: [],
      isConfigured: false,
      loading: false,
      saving: false,
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(val) { this.$emit("update:visible", val); },
    },
    allSelected() {
      return this.allForms.length > 0 && this.selectedFormIds.length === this.allForms.length;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal && this.clientId) {
        this.fetchConfig();
      }
    },
  },
  methods: {
    async fetchConfig() {
      this.loading = true;
      try {
        // Load all forms for the checklist
        const allData = await complianceFormsService.list();
        this.allForms = Array.isArray(allData) ? allData : allData.rows || [];

        // Load current config for this client
        const config = await complianceFormsService.getClientFormConfig(this.clientId);
        this.isConfigured = config.configured;
        this.selectedFormIds = config.forms.map((f) => f.id);
      } catch (err) {
        this.$notifyError("Failed to load form configuration.");
      } finally {
        this.loading = false;
      }
    },
    toggleSelectAll() {
      this.selectedFormIds = this.allSelected ? [] : this.allForms.map((f) => f.id);
    },
    async resetToDefault() {
      this.selectedFormIds = this.allForms.map((f) => f.id);
      this.isConfigured = false;
    },
    async save() {
      // If all forms are selected, save that as the explicit config
      // If none selected, treat as "reset to default" (all forms)
      const formIds = this.selectedFormIds.length === 0
        ? []
        : this.selectedFormIds;

      this.saving = true;
      try {
        await complianceFormsService.saveClientFormConfig(this.clientId, formIds);
        this.$notifySuccess("Compliance form configuration saved.");
        this.$emit("saved");
        this.dialogVisible = false;
      } catch (err) {
        this.$notifyError("Failed to save configuration.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.compliance-config__subtitle {
  color: #e0e0e0;
  margin-bottom: 1rem;
  font-size: 13px;
  line-height: 1.5;
}

.compliance-config__actions {
  display: flex;
  gap: 1rem;
}

.form-checkbox {
  display: block;
  margin-left: 0 !important;
  margin-bottom: 0.35rem;
}

:deep(.form-checkbox .el-checkbox__label) {
  color: #fff !important;
  font-size: 14px;
}
</style>
